import React from 'react';

import { useSlug } from '../hooks/useSlug';
import { ImageProps } from '../Image/index';
import { TeaseType } from '../index';
import { StoryCardSmall } from '../StoryCardSmall';
import type { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './RecircBoxSmallList.module.css';

interface Story {
  headline: string;
  id: string;
  image?: Pick<
    ImageProps,
    'src' | 'altText' | 'origWidth' | 'origHeight' | 'width' | 'height'
  >;
  publishedAt?: string;
  updatedAt?: string;
  url: string;
}

const IMAGE_DIMENSIONS = {
  width: 300,
  height: 225,
};

export type RecircBoxSmallListProps = GlobalProps & {
  // TODO - update locale to more specific type after https://jira.prod.bloomberg.com/browse/MEDIAWEBP-140 is done
  locale?: string;
  stories: Story[];
  title?: string;
  onStoryClick?: () => void;
};

export const RecircBoxSmallList = React.forwardRef<
  HTMLDivElement,
  RecircBoxSmallListProps
>(function RecircBoxSmallListComponent(
  {
    className,
    locale = 'en-US',
    name = 'RecircBoxSmallList',
    onStoryClick,
    stories = [],
    title,
    unstyled,
  }: RecircBoxSmallListProps,
  ref
) {
  const componentName = useSlug(name);

  const cx = classes([styles.wrapper, className || ''], {
    unstyled,
    name,
    className,
  });

  const storyCards = stories
    .filter(({ headline }) => Boolean(headline))
    .map(({ id, url, image, headline, publishedAt, updatedAt }) => (
      <StoryCardSmall
        className="story"
        headline={headline}
        locale={locale}
        key={id}
        publishedAt={publishedAt}
        onClick={onStoryClick}
        tease={
          image
            ? {
                type: TeaseType.Image,
                value: {
                  ...image,
                  aspectRatio: IMAGE_DIMENSIONS.width / IMAGE_DIMENSIONS.height,
                  width: IMAGE_DIMENSIONS.width,
                  height: IMAGE_DIMENSIONS.height,
                },
              }
            : undefined
        }
        updatedAt={updatedAt}
        url={url}
        unstyled={unstyled}
      />
    ));

  if (!storyCards.length) {
    return null;
  }

  return (
    <div
      className={cx}
      ref={ref}
      data-component={componentName}
      data-testid={componentName}
    >
      {title && (
        <h3 className="title" data-testid={`${componentName}--title`}>
          {title}
        </h3>
      )}
      <div className="story-wrapper">{storyCards.slice(0, 5)}</div>
    </div>
  );
});
